import { Box } from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";
import Button from "../../../../components/Button";
import DataTableMui from "../../../../components/DataTableMui";
import Icon from "../../../../components/Icons";
import Radio from "../../../../components/Inputs/Radio";
import Modal from "../../../../components/Modal";
import PageLoader from "../../../../components/PageLoader";
import { useGlobal } from "../../../../contexts/Global/context";
import { generatePDFFile, generateXMLFile, getIRYears } from "../../../../services/beneficiario/demonstrativoImpostoRenda";
import { cloneObject, downloadFile } from "../../../../utils";
import { schema } from "./schema";
import * as S from './styles';

function DemonstrativoImpostoRenda({ location: { dataBeneficiario } }) {
    const [filterValues, setFilterValues] = useState({
        ...cloneObject(schema)
    });
    const [alreadySearched, setAlreadySearched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [irData, setIrData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const { contrastTheme } = useGlobal();

    async function loadYears() {
        setLoading(true);
        try {
            const response = (await getIRYears()) || [];
            const newArray = [];

            for (let i = 0; i < response.length; i += 1) {
                newArray.push({
                    codigo: i + 1,
                    nome: response[i]
                });
            }

            return newArray;
        } catch (error) {
            return error;
        }
    }

    async function loadData() {
        setLoading(true);

        const yearOptions = (await loadYears()) || [];

        const initialValues = {
            ...filterValues,
            year: {
                ...filterValues.year,
                options: yearOptions
            }
        }

        setFilterValues({
            ...filterValues,
            ...initialValues
        });

        setLoading(false);
    }

    useEffect(() => {
        loadData();
    }, []);

    async function downloadReport(row) {
        setLoading(true);
        const body = {
            cabecalho: {
                canal: "M",
                codigoEmpresaOrigem: "1",
                divisaoNegocio: "1",
                empresaOrigemAtendimento: "1",
                origemContrato: "1",
                tipoContrato: "1",
                token: "",
                unidadeNegocio: "1"
            },
            ano: row.ano,
            carteirinha: row.numCarteira
        };

        if (row.tipoDemonstrativo === 'PDF') {
            const response = await generatePDFFile(body);

            if (!response.response && response.type !== 'application/pdf') {
                setShowModal(true);
                setLoading(false);
                return;
            }

            const file = window.URL.createObjectURL(response);
            downloadFile(`${row.numCarteira}_demonstrativo_ir.pdf`, file);

        } else {

            const response = await generateXMLFile(body);

            if (!response.data) {
                setShowModal(true);
                setLoading(false);
                return;
            }

            const blob = new Blob([response.data], { type: 'application/xml' });
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = `${row.numCarteira}_demonstrativo_ir.xml`;

            document.body.appendChild(link);
            link.click();

            URL.revokeObjectURL(url);
        }

        setLoading(false);
    }

    const [columnsHead, setColumnsHead] = useState([
        {
            key: 'numCarteira',
            selector: (row) => row.numCarteira,
            name: 'Número da carteira',
            sortable: true,
            direction: 'left',
            customStyles: () => ({ padding: '0 16px 0 24px', width: '200px' }),
        },
        {
            key: 'nomeCompleto',
            selector: (row) => row.nomeCompleto,
            name: 'Nome completo',
            sortable: true,
            direction: 'left',
            customStyles: () => ({ padding: '0 16px 0 24px', width: '200px' }),
        },
        {
            key: 'tipoBeneficiario',
            selector: (row) => row.tipoBeneficiario,
            name: 'Tipo beneficiário',
            sortable: true,
            direction: 'left',
            customStyles: () => ({ padding: '0 16px 0 24px', width: '200px' }),
        },
        {
            key: 'tipoDemonstrativo',
            selector: (row) => row.tipoDemonstrativo,
            name: 'Tipo demonstrativo',
            sortable: true,
            direction: 'left',
            customStyles: () => ({ padding: '0 16px 0 24px', width: '200px' }),
        },
        {
            key: 'download',
            selector: (row) => row,
            name: 'Download',
            sortable: true,
            direction: 'center',
            mutation: (row) => (
                <S.CellWithIcon>
                    <Icon
                        name='file_download'
                        color={contrastTheme ? '#FFF333' : '#EC6E0C'}
                        onClick={() => downloadReport(row, filterValues.year.lookup)}
                        tooltip={<S.TooltipText>Baixar arquivo gerado</S.TooltipText>}
                        size={22}
                    />
                </S.CellWithIcon>
            ),
            customStyles: () => ({ padding: '0 16px 0 24px', width: '200px' }),
        }
    ]);



    function handleColumns() {
        const newCollumnsHead = [...columnsHead];

        setColumnsHead([...newCollumnsHead]);
    }

    async function doSearch() {
        setIsLoading(true);
        const data = [
            {
                numCarteira: dataBeneficiario.numCarteira,
                nomeCompleto: dataBeneficiario.nomeCompleto,
                tipoBeneficiario: dataBeneficiario.tipo,
                tipoDemonstrativo: filterValues.fileType.lookup,
                ano: filterValues.year.lookup
            }
        ];

        setIrData(data);
        setIsLoading(false);
    }

    async function handleGenerateFile() {
        setIsLoading(true);

        if (filterValues.year.lookup) {
            await doSearch();
        }

        handleColumns();

        setIsLoading(false);
        setAlreadySearched(true);
    }

    function handleChangeInput(event) {
        const { id, value, ...rest } = event;

        const field = { ...filterValues[id], value, error: '', ...rest };

        const editedForm = { ...filterValues, [id]: field };

        setFilterValues(editedForm);

    }
    const handleClickClearAll = useCallback(() => {
        setIrData([]);

        const oldFilterValues = filterValues;

        delete oldFilterValues.year.value;

        setFilterValues({ ...oldFilterValues });
    }, [irData, filterValues]);

    return (
        <>
            <Box padding='32px'>
                <S.Texto>
                    <p>Você poderá obter demonstrativo de imposto de renda em .pdf ou .xml.</p>
                </S.Texto>
                <Box width="100%">
                    <S.SearchContainer display="flex">
                        <S.StyledSelect field={{
                            ...filterValues.year
                        }}
                            onChange={handleChangeInput}
                        />

                        <Radio field={filterValues.fileType} onChange={handleChangeInput} />
                        <S.SearchButton type="primary" onClick={handleGenerateFile}>
                            Gerar demonstrativo
                        </S.SearchButton>
                    </S.SearchContainer>
                </Box>
                {irData.length > 0 && (
                    <S.LinkButtonRegister id="clearRegisters" onClick={handleClickClearAll}>
                        <span className="active">Limpar registros</span>
                    </S.LinkButtonRegister>
                )}
                <S.TableContainer>
                    <DataTableMui
                        alreadySearched={alreadySearched}
                        columns={columnsHead}
                        data={irData}
                        emptyMessage="Dados não encontrados."
                        loading={isLoading}
                    />
                </S.TableContainer>
                <Modal
                    title="Atenção"
                    subtitle="Não foi possível gerar o demonstrativo de imposto de renda, por gentileza entrar em contato com a NDI."
                    visible={showModal}
                    onCancel={() => setShowModal(false)}
                    footer={null}
                >
                    <Button
                        type="primary"
                        onClick={() => {
                            setShowModal(false);
                        }}
                    >
                        Entendi
                    </Button>
                </Modal>
                <PageLoader loading={loading} message="Gerando o demonstravido de imposto de renda... Aguarde." />
            </Box>

        </>
    );
}

export default DemonstrativoImpostoRenda;